/**
 * Static seed actions for the command palette — mostly navigation links.
 * Dynamic actions (per-template runs, pins, etc.) are registered by their
 * owning page via `useRegisterCommands`.
 */

import {
  Activity,
  BarChart3,
  CheckSquare,
  Database,
  FileText,
  Home,
  Inbox,
  Network,
  Settings,
  Shield,
  SlidersHorizontal,
  Users,
} from 'lucide-react';

import type { CommandAction } from './registry';

/** Builds the navigation action set. Pass in react-router's `navigate`. */
export function createNavigationActions(
  navigate: (path: string) => void,
): CommandAction[] {
  return [
    {
      id: 'nav:dashboard',
      label: 'Go to Dashboard',
      section: 'navigate',
      icon: Home,
      keywords: ['home', 'chat'],
      onSelect: () => navigate('/'),
    },
    {
      id: 'nav:reports',
      label: 'Go to Reports',
      section: 'navigate',
      icon: BarChart3,
      keywords: ['library', 'analytics', 'report'],
      onSelect: () => navigate('/reports'),
    },
    {
      id: 'nav:documents',
      label: 'Go to Documents',
      section: 'navigate',
      icon: FileText,
      keywords: ['files', 'upload'],
      onSelect: () => navigate('/documents'),
    },
    {
      id: 'nav:inbox',
      label: 'Go to Inbox',
      section: 'navigate',
      icon: Inbox,
      keywords: ['review', 'pending'],
      onSelect: () => navigate('/inbox'),
    },
    {
      id: 'nav:entity-decisions',
      label: 'Go to Entity Decisions',
      section: 'navigate',
      icon: CheckSquare,
      keywords: ['resolution', 'merges'],
      onSelect: () => navigate('/entity-decisions'),
    },
    {
      id: 'nav:connections',
      label: 'Go to Connections',
      section: 'navigate',
      icon: Database,
      keywords: ['integrations', 'sources'],
      onSelect: () => navigate('/connections'),
    },
    {
      id: 'nav:rules',
      label: 'Go to Rules',
      section: 'navigate',
      icon: Shield,
      keywords: ['business rules', 'policies'],
      onSelect: () => navigate('/rules'),
    },
    {
      id: 'nav:agents',
      label: 'Go to Agents',
      section: 'navigate',
      icon: Users,
      onSelect: () => navigate('/agents'),
    },
    {
      id: 'nav:pipeline',
      label: 'Go to Pipeline',
      section: 'navigate',
      icon: Activity,
      keywords: ['processing', 'funnel'],
      onSelect: () => navigate('/pipeline'),
    },
    {
      id: 'nav:graph',
      label: 'Go to Graph Explorer',
      section: 'navigate',
      icon: Network,
      onSelect: () => navigate('/graph'),
    },
    {
      id: 'nav:what-if',
      label: 'Go to What-If',
      section: 'navigate',
      icon: SlidersHorizontal,
      keywords: ['simulation', 'scenario'],
      onSelect: () => navigate('/what-if'),
    },
    {
      id: 'nav:settings',
      label: 'Go to Settings',
      section: 'navigate',
      icon: Settings,
      onSelect: () => navigate('/settings'),
    },
    {
      id: 'nav:team',
      label: 'Go to Team',
      section: 'navigate',
      icon: Users,
      keywords: ['invite', 'people', 'roles', 'members'],
      onSelect: () => navigate('/team'),
    },
  ];
}
